import { useEffect, useLayoutEffect, useRef, type ComponentProps, type ReactNode } from 'react';
import { ThreadComposer } from './ThreadComposer';
import { ThreadMessages } from './ThreadMessages';

/** How close to the bottom still counts as "read to the end". */
const BOTTOM_SLACK_PX = 24;

interface ThreadViewProps {
	messages: ComponentProps<typeof ThreadMessages>;
	/** Omit for a read-only thread (the supplier can no longer post, say). */
	composer?: ComponentProps<typeof ThreadComposer>;
	/** Shown instead of the composer when there is none — why posting isn't possible. */
	readOnlyNote?: ReactNode;
	loading?: boolean;
	/** Fired when the reader reaches the end — the app marks the thread read. */
	onReachBottom?: () => void;
	className?: string;
}

/**
 * A thread as one unit: the messages in a scrolling area that follows the
 * newest one, and the composer pinned beneath. Fills its parent's height.
 */
export function ThreadView({
	messages,
	composer,
	readOnlyNote,
	loading,
	onReachBottom,
	className,
}: ThreadViewProps) {
	const scrollRef = useRef<HTMLDivElement>(null);
	const count = messages.messages.length;
	const lastId = messages.messages[count - 1]?.id;

	// Follow the newest message — on open and whenever one is added.
	useLayoutEffect(() => {
		const el = scrollRef.current;
		if (el) el.scrollTop = el.scrollHeight;
	}, [lastId, loading]);

	// A thread that fits without scrolling has been read the moment it shows.
	useEffect(() => {
		const el = scrollRef.current;
		if (!el || loading || count === 0) return;
		if (el.scrollHeight - el.clientHeight - el.scrollTop <= BOTTOM_SLACK_PX) onReachBottom?.();
	}, [lastId, loading, count, onReachBottom]);

	return (
		<div className={['flex min-h-0 flex-col', className].filter(Boolean).join(' ')}>
			<div
				ref={scrollRef}
				onScroll={(e) => {
					const el = e.currentTarget;
					if (el.scrollHeight - el.clientHeight - el.scrollTop <= BOTTOM_SLACK_PX) {
						onReachBottom?.();
					}
				}}
				className="scroll-thin min-h-0 flex-1 overflow-y-auto px-4 py-4"
			>
				{loading && count === 0 ? (
					<div className="flex flex-col gap-3">
						{[0, 1, 2].map((i) => (
							<div
								key={i}
								className="h-12 animate-pulse rounded-[var(--r-3)] bg-surface-fill"
							/>
						))}
					</div>
				) : (
					<ThreadMessages {...messages} />
				)}
			</div>
			<div className="border-t border-line-1 px-4 py-3">
				{composer ? (
					<ThreadComposer {...composer} />
				) : (
					<div className="text-sm text-text-4">{readOnlyNote}</div>
				)}
			</div>
		</div>
	);
}
